// Filter chips component for quick filtering
// Premium micro-interactions with smooth transitions

import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import type { ComponentCategory, ConditionState, RestorePhase } from '@/types';

interface FilterChipsProps {
  selectedComponent?: ComponentCategory;
  selectedCondition?: ConditionState;
  selectedPhase?: RestorePhase;
  onComponentChange: (component?: ComponentCategory) => void;
  onConditionChange: (condition?: ConditionState) => void;
  onPhaseChange: (phase?: RestorePhase) => void;
  onResetAll?: () => void;
  className?: string;
}

const COMPONENT_OPTIONS: { value: ComponentCategory; label: string }[] = [
  { value: 'engine', label: 'Engine' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'interior', label: 'Interior' },
  { value: 'body', label: 'Body' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'drivetrain', label: 'Drivetrain' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'steering', label: 'Steering' },
];

const CONDITION_OPTIONS: { value: ConditionState; label: string }[] = [
  { value: 'as-received', label: 'As Received' },
  { value: 'disassembly', label: 'Disassembly' },
  { value: 'restored', label: 'Restored' },
  { value: 'installed', label: 'Installed' },
];

export function FilterChips({
  selectedComponent,
  selectedCondition,
  selectedPhase,
  onComponentChange,
  onConditionChange,
  onPhaseChange,
  onResetAll,
  className,
}: FilterChipsProps) {
  return (
    <div className={clsx('space-y-3', className)}>
      {/* Component filters */}
      <div className="flex flex-wrap gap-2">
        <FilterChip
          label="All"
          isSelected={!selectedComponent && !selectedCondition}
          onClick={() => {
            // Reset all filters when clicking "All"
            if (onResetAll) {
              onResetAll();
            } else {
              // Fallback to individual resets
              onComponentChange(undefined);
              onConditionChange(undefined);
            }
          }}
        />
        {COMPONENT_OPTIONS.map((option, index) => (
          <FilterChip
            key={option.value}
            label={option.label}
            isSelected={selectedComponent === option.value}
            onClick={() =>
              onComponentChange(
                selectedComponent === option.value ? undefined : option.value
              )
            }
            index={index}
          />
        ))}
      </div>

      {/* Condition filters - always visible */}
      <div className="flex flex-wrap gap-2">
        {CONDITION_OPTIONS.map((option, index) => (
          <FilterChip
            key={option.value}
            label={option.label}
            isSelected={selectedCondition === option.value}
            onClick={() =>
              onConditionChange(
                selectedCondition === option.value ? undefined : option.value
              )
            }
            variant="secondary"
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  index?: number;
}

function FilterChip({
  label,
  isSelected,
  onClick,
  variant = 'primary',
  index = 0,
}: FilterChipProps) {
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: { delay: index * 0.02, duration: 0.15 }
      }}
      className={clsx(
        'px-4 py-2.5 rounded-xl font-medium',
        'min-h-touch flex items-center justify-center',
        'text-workshop-sm',
        'transition-colors duration-150',
        variant === 'primary' && [
          isSelected
            ? 'bg-workshop-accent text-white shadow-lg shadow-workshop-accent/25'
            : 'bg-workshop-surface text-gray-300 hover:bg-workshop-border',
        ],
        variant === 'secondary' && [
          isSelected
            ? 'bg-workshop-success/20 text-workshop-success border border-workshop-success'
            : 'bg-workshop-surface text-gray-400 hover:bg-workshop-border border border-transparent',
        ]
      )}
    >
      {label}
    </motion.button>
  );
}
